/**
 * DropZone — drag-and-drop + click-to-browse file selector.
 *
 * Only accepts PDF files. Client-side size is validated here before upload.
 */

import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface DropZoneProps {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
}

const MAX_SIZE_BYTES = 30 * 1024 * 1024; // 30 MB

export const DropZone: React.FC<DropZoneProps> = ({
  onFileSelected,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback(
    (file: File): string | null => {
      if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
        return t("dropzone.errorPdfOnly");
      }
      if (file.size > MAX_SIZE_BYTES) {
        return t("dropzone.errorTooLarge", {
          sizeMb: (file.size / 1024 / 1024).toFixed(1),
        });
      }
      return null;
    },
    [t]
  );

  const handleFile = useCallback(
    (file: File) => {
      const error = validate(file);
      if (error) {
        setValidationError(error);
        return;
      }
      setValidationError(null);
      onFileSelected(file);
    },
    [validate, onFileSelected]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset input so the same file can be re-selected
      e.target.value = "";
    },
    [handleFile]
  );

  return (
    <div className="dropzone-wrapper">
      <div
        className={[
          "dropzone",
          isDragOver ? "dropzone--active" : "",
          disabled ? "dropzone--disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={disabled ? undefined : onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={t("dropzone.ariaLabel")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={onInputChange}
          disabled={disabled}
        />

        <div className="dropzone__icon">📄</div>
        <p className="dropzone__primary">
          {isDragOver ? t("dropzone.dragActive") : t("dropzone.dragPrompt")}
        </p>
        <p className="dropzone__secondary">{t("dropzone.browsePrompt")}</p>
      </div>

      {validationError && (
        <p className="dropzone__error" role="alert">
          {validationError}
        </p>
      )}
    </div>
  );
};
